import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

const DirectPurchase = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [bypassBalance, setBypassBalance] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDuration, setSelectedDuration] = useState(1);
  const { success, error } = useNotification();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersResponse, productsResponse] = await Promise.all([
        axios.get('/api/admin/users', { params: { limit: 100 } }),
        axios.get('/api/products')
      ]);
      setUsers(usersResponse.data.users);
      setProducts(productsResponse.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    if (!selectedUser || !selectedProduct) return;

    setPurchasing(true);
    try {
      const response = await axios.post(
        `/api/admin/users/${selectedUser}/purchase/${selectedProduct}`,
        { 
          bypassBalance: bypassBalance,
          duration: selectedDuration,
          useResellerCredits: user?.role === 'reseller'
        }
      );
      
      success(`License key purchased successfully for user!\nKey: ${response.data.licenseKey.key}\nDuration: ${selectedDuration} days\nYour remaining credits: ${response.data.resellerCredits || 'N/A'}`, 8000);
      
      // Reset form
      setSelectedUser('');
      setSelectedProduct('');
      setSelectedDuration(1);
      setBypassBalance(false);
      
      // Refresh data
      fetchData();
    } catch (err) {
      error(err.response?.data?.message || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  const calculateCost = (product, days) => {
    if (!product) return 0;
    return product.baseCreditCost + (days * product.creditsPerDay);
  };

  const selectedUserData = users.find(u => u._id === selectedUser);
  const selectedProductData = products.find(p => p._id === selectedProduct);

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card-astral p-6">
        <h2 className="text-2xl font-bold text-white mb-2">
          {user?.role === 'reseller' ? 'Reseller Purchase' : 'Direct Purchase'}
        </h2>
        <p className="text-gray-400">
          {user?.role === 'reseller' 
            ? 'Purchase license keys for users using YOUR credits. The keys will be assigned to the selected user, but charged to your account.'
            : 'Purchase license keys directly for users as an administrator.'
          }
        </p>
        
        {user?.role === 'reseller' && (
          <div className="mt-4 p-3 bg-orange-600/20 border border-orange-500/50 rounded-lg">
            <div className="flex items-center text-orange-400">
              <span className="text-lg mr-2">💳</span>
              <span className="font-semibold">Your Credits: {user?.credits || 0}</span>
            </div>
            <p className="text-orange-300 text-sm mt-1">
              Keys will be charged to YOUR account but assigned to the selected user.
            </p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Purchase Form */}
        <div className="card-astral p-6">
          <h3 className="text-xl font-bold text-white mb-4">Purchase Form</h3>
          
          <form onSubmit={handlePurchase} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Search and Select User
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users..."
                className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white mb-2"
              />
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
                required
              >
                <option value="">Choose a user...</option>
                {filteredUsers.map((targetUser) => (
                  <option key={targetUser._id} value={targetUser._id}>
                    {targetUser.username} ({targetUser.email}) - {targetUser.credits} credits
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Select Product
              </label>
              <select
                value={selectedProduct}
                onChange={(e) => {
                  setSelectedProduct(e.target.value);
                  const product = products.find(p => p._id === e.target.value);
                  if (product) {
                    setSelectedDuration(product.minDays || 1);
                  }
                }}
                className="w-full px-4 py-3 bg-dark-200 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-400"
                required
              >
                <option value="">Choose a product...</option>
                {products.map((product) => (
                  <option key={product._id} value={product._id}>
                    {product.name} - Base: {product.baseCreditCost} + {product.creditsPerDay}/day credits
                  </option>
                ))}
              </select>
            </div>

            {selectedProductData && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  License Duration ({selectedProductData.minDays}-{selectedProductData.maxDays} days)
                </label>
                <div className="space-y-2">
                  <input
                    type="range"
                    min={selectedProductData.minDays}
                    max={selectedProductData.maxDays}
                    value={selectedDuration}
                    onChange={(e) => setSelectedDuration(parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>{selectedProductData.minDays} days</span>
                    <span className="text-white font-semibold">{selectedDuration} days</span>
                    <span>{selectedProductData.maxDays} days</span>
                  </div>
                </div>
              </div>
            )}

            {user?.role === 'owner' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="bypassBalance"
                  checked={bypassBalance}
                  onChange={(e) => setBypassBalance(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="bypassBalance" className="text-sm text-gray-300">
                  Bypass balance check (free purchase)
                </label>
              </div>
            )}

            {selectedUserData && selectedProductData && (
              <div className="bg-dark-200 p-4 rounded-lg space-y-2">
                <h4 className="text-white font-semibold mb-2">Purchase Summary</h4>
                <div className="flex justify-between">
                  <span className="text-gray-400">Target User:</span>
                  <span className="text-white">{selectedUserData.username}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Product:</span>
                  <span className="text-white">{selectedProductData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Duration:</span>
                  <span className="text-white">{selectedDuration} days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Base cost:</span>
                  <span className="text-blue-400">{selectedProductData.baseCreditCost} credits</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Duration cost:</span>
                  <span className="text-green-400">{selectedDuration} × {selectedProductData.creditsPerDay} = {selectedDuration * selectedProductData.creditsPerDay} credits</span>
                </div>
                <div className="flex justify-between border-t border-gray-600 pt-2">
                  <span className="text-gray-400 font-semibold">Total cost:</span>
                  <span className="text-purple-400 font-semibold">{calculateCost(selectedProductData, selectedDuration)} credits</span>
                </div>
                
                {user?.role === 'reseller' ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Your credits:</span>
                      <span className="text-white">{user?.credits || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">After purchase:</span>
                      <span className={`font-semibold ${
                        (user?.credits || 0) >= calculateCost(selectedProductData, selectedDuration)
                          ? 'text-green-400'
                          : 'text-red-400'
                      }`}>
                        {(user?.credits || 0) - calculateCost(selectedProductData, selectedDuration)} credits
                      </span>
                    </div>
                    <div className="text-orange-400 text-sm border-t border-gray-600 pt-2">
                      🏷️ Reseller Purchase: Charged to YOUR account, assigned to {selectedUserData.username}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-400">User credits:</span>
                      <span className="text-white">{selectedUserData.credits}</span>
                    </div>
                    {!bypassBalance && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">After purchase:</span>
                        <span className={`font-semibold ${
                          selectedUserData.credits >= calculateCost(selectedProductData, selectedDuration)
                            ? 'text-green-400'
                            : 'text-red-400'
                        }`}>
                          {selectedUserData.credits - calculateCost(selectedProductData, selectedDuration)} credits
                        </span>
                      </div>
                    )}
                  </>
                )}
                
                {bypassBalance && user?.role === 'owner' && (
                  <div className="text-yellow-400 text-sm border-t border-gray-600 pt-2">
                    ⚠️ Balance check bypassed - Purchase will be free
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={!selectedUser || !selectedProduct || purchasing || 
                       (user?.role === 'reseller' && selectedProductData && 
                        (user?.credits || 0) < calculateCost(selectedProductData, selectedDuration)) ||
                       (user?.role !== 'reseller' && selectedUserData && selectedProductData && 
                        selectedUserData.credits < calculateCost(selectedProductData, selectedDuration) && !bypassBalance)}
              className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-300 ${
                selectedUser && selectedProduct && 
                ((user?.role === 'reseller' && (user?.credits || 0) >= calculateCost(selectedProductData, selectedDuration)) ||
                 (user?.role !== 'reseller' && (bypassBalance || (selectedUserData?.credits >= calculateCost(selectedProductData, selectedDuration)))))
                  ? 'btn-neon' 
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {purchasing ? (
                <div className="flex items-center justify-center">
                  <div className="spinner mr-2"></div>
                  Processing Purchase...
                </div>
              ) : !selectedUser || !selectedProduct ? (
                'Select User and Product'
              ) : user?.role === 'reseller' && selectedProductData && 
                (user?.credits || 0) < calculateCost(selectedProductData, selectedDuration) ? (
                'Insufficient YOUR Credits'
              ) : user?.role !== 'reseller' && selectedUserData && selectedProductData && 
                selectedUserData.credits < calculateCost(selectedProductData, selectedDuration) && !bypassBalance ? (
                'Insufficient User Credits'
              ) : (
                `Purchase for ${calculateCost(selectedProductData, selectedDuration)} credits ${
                  user?.role === 'reseller' ? '(FROM YOUR ACCOUNT)' : 
                  bypassBalance && user?.role === 'owner' ? '(Free)' : '(FROM USER ACCOUNT)'
                }`
              )}
            </button>
          </form>
        </div>

        {/* Available Products */}
        <div className="card-astral p-6">
          <h3 className="text-xl font-bold text-white mb-4">Available Products</h3>
          
          {products.length === 0 ? (
            <p className="text-gray-400">No products available.</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {products.map((product) => (
                <div key={product._id} className="bg-dark-200 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-white">{product.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-2 py-1 bg-purple-600/20 text-purple-400 text-xs rounded font-mono">
                          {product.keyTag}
                        </span>
                        <span className="px-2 py-1 bg-blue-600/20 text-blue-400 text-xs rounded capitalize">
                          {product.productType}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-green-400 font-semibold text-sm">
                        {product.baseCreditCost} + {product.creditsPerDay}/day
                      </div>
                      <div className="text-xs text-gray-400">credits</div>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm">{product.description}</p>
                  
                  <div className="mt-2 p-2 bg-dark-100 rounded">
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-400">1 day:</span>
                        <span className="text-white">{product.baseCreditCost + product.creditsPerDay} credits</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">7 days:</span>
                        <span className="text-white">{product.baseCreditCost + (7 * product.creditsPerDay)} credits</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">30 days:</span>
                        <span className="text-white">{product.baseCreditCost + (30 * product.creditsPerDay)} credits</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-2 text-xs text-gray-500">
                    Duration: {product.minDays}-{product.maxDays} days
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DirectPurchase;
